import { useAuth } from 'Components/Auth/hooks'
import { useMutation, useQuery, useQueryClient } from 'react-query'

import {
  makeExportTournamentTeamsApiPath,
  makeTournamentAnnouncementApiPath,
  makeTournamentAnnouncementsApiPath,
  makeTournamentApiPath,
  makeTournamentBookmarkMapsApiPath,
  makeTournamentCommentsPath,
  makeTournamentMyTeamApiPath,
  makeTournamentsApiPath,
  makeTournamentTeamApiPath,
  makeTournamentTeamsApiPath,
} from './constants'

export const useSaveTournamentMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useMutation(
    (payload) =>
      axios[tournamentId ? 'put' : 'post'](tournamentId ? TOURNAMENT_URL : TOURNAMENTS_URL, payload, {
        headers: { 'content-type': 'multipart/form-data' },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(TOURNAMENTS_URL)
        queryClient.invalidateQueries(TOURNAMENT_URL)
      },
    },
  )
}

export const useSaveTournamentTeamMutation = ({ tournamentId, teamId }) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)
  const TEAMS_URL = makeTournamentTeamsApiPath(tournamentId)
  const TEAM_URL = makeTournamentTeamApiPath(tournamentId, teamId)
  const MY_TEAM_URL = makeTournamentMyTeamApiPath(tournamentId)

  return useMutation((payload) => axios[teamId ? 'put' : 'post'](teamId ? TEAM_URL : TEAMS_URL, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(TEAMS_URL)
      queryClient.invalidateQueries(TEAM_URL)
      queryClient.invalidateQueries(MY_TEAM_URL)
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useTournamentsQuery = (queryString = '') => {
  const { axios } = useAuth()
  const TOURNAMENTS_URL = makeTournamentsApiPath()

  return useQuery([TOURNAMENTS_URL, queryString], async () => {
    const params = {
      _limit: 999,
    }
    const { data } = await axios.get(`${TOURNAMENTS_URL}?${queryString}`, { params })

    return data?.data
  })
}

export const useTournamentsInProgressQuery = () =>
  useTournamentsQuery('_null_time_tournament_end=true&_null_time_archived=true')

export const useTournamentsFinishedQuery = () =>
  useTournamentsQuery('_not_null_time_tournament_end=true&_null_time_archived=true')

export const useTournamentsArchivedQuery = () => useTournamentsQuery('_not_null_time_archived=true')

export const useTournamentTeamsQuery = (tournamentId, options = {}) => {
  const { axios } = useAuth()
  const TEAMS_URL = makeTournamentTeamsApiPath(tournamentId)

  return useQuery(
    [TEAMS_URL],
    async () => {
      const params = {
        _limit: 999,
      }
      const { data } = await axios.get(TEAMS_URL, { params })
      return data
    },
    {
      ...options,
      enabled: !!tournamentId,
    },
  )
}

export const useTournamentByIdQuery = (tournamentId, options = {}) => {
  const { axios } = useAuth()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useQuery(
    [TOURNAMENT_URL],
    async () => {
      const { data } = await axios.get(TOURNAMENT_URL)

      return data
    },
    {
      ...options,
      enabled: !!tournamentId,
    },
  )
}

export const useUserTeamByTournamentIdQuery = (tournamentId) => {
  const { axios, isAuthorized } = useAuth()
  const MY_TEAM_URL = makeTournamentMyTeamApiPath(tournamentId)

  return useQuery(
    [MY_TEAM_URL],
    async () => {
      const { data } = await axios.get(MY_TEAM_URL)
      return data.data[0]
    },
    { enabled: !!tournamentId && isAuthorized },
  )
}

export const usePublishTournamentResultMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useMutation((results) => axios.post(`${TOURNAMENT_URL}/results`, { results }), {
    onSuccess: () => {
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useDeleteTournamentTeamMutation = ({ tournamentId, teamId }) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)
  const TEAMS_URL = makeTournamentTeamsApiPath(tournamentId)
  const TEAM_URL = makeTournamentTeamApiPath(tournamentId, teamId)
  const MY_TEAM_URL = makeTournamentMyTeamApiPath(tournamentId)

  return useMutation(() => axios.delete(TEAM_URL), {
    onSuccess: () => {
      queryClient.invalidateQueries(TEAMS_URL)
      queryClient.invalidateQueries(TEAM_URL)
      queryClient.invalidateQueries(MY_TEAM_URL)
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useArchiveTournamentMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useMutation(() => axios.post(`${TOURNAMENT_URL}/archive`), {
    onSuccess: () => {
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useRestoreTournamentMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useMutation(() => axios.delete(`${TOURNAMENT_URL}/archive`), {
    onSuccess: () => {
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useDeleteTournamentMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENTS_URL = makeTournamentsApiPath()
  const TOURNAMENT_URL = makeTournamentApiPath(tournamentId)

  return useMutation(() => axios.delete(TOURNAMENT_URL), {
    onSuccess: () => {
      queryClient.invalidateQueries(TOURNAMENTS_URL)
      queryClient.invalidateQueries(TOURNAMENT_URL)
    },
  })
}

export const useGetTournamentAnnouncementMutation = (tournamentId) => {
  const { axios } = useAuth()
  const TOURNAMENT_ANNOUNCEMENTS_URL = makeTournamentAnnouncementsApiPath(tournamentId)

  return useQuery(
    [TOURNAMENT_ANNOUNCEMENTS_URL],
    async () => {
      const { data } = await axios.get(makeTournamentAnnouncementsApiPath(tournamentId))
      return data?.data
    },
    {
      enabled: !!tournamentId,
    },
  )
}

export const useAddTournamentAnnouncementMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENT_ANNOUNCEMENTS_URL = makeTournamentAnnouncementsApiPath(tournamentId)

  return useMutation((payload) => axios.post(makeTournamentAnnouncementsApiPath(tournamentId), payload), {
    onSettled: () => {
      queryClient.invalidateQueries(TOURNAMENT_ANNOUNCEMENTS_URL)
    },
  })
}

export const useEditTournamentAnnouncementMutation = (tournamentId, id) => {
  const { axios } = useAuth()
  return useMutation((payload) => axios.put(makeTournamentAnnouncementApiPath(tournamentId, id), payload))
}

export const useDeleteTournamentAnnouncementMutation = (tournamentId, id) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const TOURNAMENT_ANNOUNCEMENTS_URL = makeTournamentAnnouncementsApiPath(tournamentId)

  return useMutation(() => axios.delete(makeTournamentAnnouncementApiPath(tournamentId, id)), {
    onSettled: () => {
      queryClient.invalidateQueries(TOURNAMENT_ANNOUNCEMENTS_URL)
    },
  })
}

export const useBookmarkTournamentMapsMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const query = makeTournamentBookmarkMapsApiPath(tournamentId)

  return useMutation(({ queryType }) => axios[queryType](query), {
    onSuccess: () => {
      queryClient.invalidateQueries(makeTournamentApiPath(tournamentId))
    },
  })
}

export const useAddTournamentCommentMutation = (tournamentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(tournamentId)

  return useMutation((payload) => axios.post(path, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useAddTournamentReplyMutation = (tournamentId, commentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(tournamentId)

  return useMutation((payload) => axios.post(`${path}/${commentId}`, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useEditTournamentCommentMutation = (tournamentId, commentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(tournamentId)

  return useMutation((payload) => axios.put(`${path}/${commentId}`, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useDeleteTournamentCommentMutation = (tournamentId, commentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(tournamentId)

  return useMutation(() => axios.delete(`${path}/${commentId}`), {
    onSuccess: () => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useReactOnTournamentCommentMutation = (id, commentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(id)

  return useMutation(({ type }) => axios.post(`${path}/${commentId}/likes/${type}`), {
    onSuccess: (_) => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useDeleteTournamentCommentReactionMutation = (id, commentId) => {
  const { axios } = useAuth()
  const queryClient = useQueryClient()
  const path = makeTournamentCommentsPath(id)

  return useMutation(({ type }) => axios.delete(`${path}/${commentId}/likes/${type}`), {
    onSuccess: () => {
      queryClient.invalidateQueries([path])
    },
  })
}

export const useExportTournamentTeamsMutation = (id) => {
  const { axios } = useAuth()

  return useMutation(async () => {
    const response = await axios.get(makeExportTournamentTeamsApiPath(id), {
      responseType: 'blob',
    })

    const href = URL.createObjectURL(response.data)

    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', `tournament_teams_${id}.xlsx`)
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    URL.revokeObjectURL(href)
  })
}
